class CartUpsell extends HTMLElement {
  connectedCallback() {
    this._load();

    // The cart drawer re-renders this element's markup from the server on every
    // cart mutation (add/remove/quantity change) without reconnecting it, which
    // wipes out the JS-populated content and puts the "hidden" class back.
    // Watch for that and reload, same pattern the theme's own
    // product-recommendations component uses for this exact problem.
    this._mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          this.classList.contains('hidden')
        ) {
          this._load();
          break;
        }
      }
    });
    this._mutationObserver.observe(this, { attributes: true, attributeFilter: ['class'] });
  }

  disconnectedCallback() {
    this._mutationObserver?.disconnect();
  }

  async _load() {
    const seedId = this.dataset.seedId;
    const baseUrl = this.dataset.recommendationsUrl;
    if (!seedId || !baseUrl) return;

    const excludeIds = (this.dataset.cartProductIds || '')
      .split(',')
      .map((id) => parseInt(id, 10))
      .filter(Boolean);

    try {
      const [recRes, cart] = await Promise.all([
        fetch(`${baseUrl}.json?product_id=${seedId}&limit=8&intent=related`),
        fetch('/cart.js').then((r) => r.json()),
      ]);
      if (!recRes.ok) throw new Error('bad response');
      const data = await recRes.json();
      const products = (data.products || []).filter((p) => !excludeIds.includes(p.id));
      if (!products.length) return;

      const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: cart.currency });
      const track = this.querySelector('[data-cart-upsell-track]');
      track.innerHTML = products.slice(0, 4).map((p) => {
        const variant = p.variants[0];
        const image = p.images && p.images[0] ? p.images[0] : '';
        return `
          <div class="cart-upsell__card">
            <a href="${p.url}" class="cart-upsell__image-link">
              <img src="${image}" alt="${p.title}" loading="lazy" class="cart-upsell__image">
            </a>
            <p class="cart-upsell__title">${p.title}</p>
            <p class="cart-upsell__price">${formatter.format(variant.price / 100)}</p>
            <button type="button" class="cart-upsell__add" data-variant-id="${variant.id}">Add</button>
          </div>
        `;
      }).join('');

      track.querySelectorAll('[data-variant-id]').forEach((btn) => {
        btn.addEventListener('click', () => this._addToCart(btn));
      });

      this.classList.remove('hidden');
    } catch (e) {
      // Leave hidden on any failure.
    }
  }

  async _addToCart(btn) {
    const variantId = btn.dataset.variantId;
    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = 'Adding...';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('add failed');

      const cartData = await fetch('/cart.js').then((r) => r.json());
      const updateEvent = new Event('shopify:cart:lines-update', { bubbles: true });
      updateEvent.action = 'add';
      updateEvent.context = 'cart';
      updateEvent.lines = [{ merchandiseId: String(variantId), quantity: 1 }];
      updateEvent.promise = Promise.resolve({
        cart: {
          id: cartData.token,
          totalQuantity: cartData.item_count,
          cost: { totalAmount: { amount: String(cartData.total_price / 100), currencyCode: cartData.currency } },
          lines: [],
          discountCodes: [],
        },
        detail: {
          items: cartData.items,
          itemCount: cartData.item_count,
          source: 'cart-upsell',
          didError: false,
        },
      });
      document.dispatchEvent(updateEvent);

      btn.textContent = 'Added';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    } catch (e) {
      btn.textContent = 'Error';
      setTimeout(() => {
        btn.textContent = originalText;
        btn.disabled = false;
      }, 1500);
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
